import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import translations from "./translations";

type Language = "en" | "pt";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, any>) => string;
  formatNumber: (value: number, decimals?: number) => string;
}

const translationData = {
  en: translations.en,
  pt: translations.pt,
} as const;

const LanguageContext = createContext<LanguageContextType | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>("pt"); // Default to Portuguese

  const t = useCallback((key: string, params?: Record<string, any>) => {
    const keys = key.split(".");
    let value: any = translationData[language];

    for (const k of keys) {
      if (value === undefined) return key;
      value = value[k];
    }

    if (!value) return key;
    
    // Handle parameter interpolation if params are provided
    if (params) {
      return Object.entries(params).reduce((acc, [paramKey, paramValue]) => {
        // Ensure acc is a string before calling replace
        if (typeof acc !== 'string') {
          return String(acc).replace(new RegExp(`%{${paramKey}}`, 'g'), String(paramValue));
        }
        return acc.replace(new RegExp(`%{${paramKey}}`, 'g'), String(paramValue));
      }, value);
    }

    return value;
  }, [language]);

  // Format number based on language
  const formatNumber = useCallback((value: number, decimals = 2) => {
    const locale = language === 'en' ? 'en-US' : 'pt-BR';
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value);
  }, [language]);

  const value = {
    language,
    setLanguage,
    t,
    formatNumber,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useTranslation must be used within a LanguageProvider");
  }
  return context;
}